import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import type { CardMetadata } from '../../types/card';
import type { AddCardFn, SetThumbnailFn } from '../../types/addCard';
import SortableCard from './SortableCard';
import PortalCard from './PortalCard';
import EmptyState from './EmptyState';
import AddCardInline from './AddCardInline';

interface Props {
  cards: CardMetadata[];
  loading: boolean;
  hasSection: boolean;
  onAddCard?: AddCardFn;
  onUpdateCard?: (id: string, updates: { title: string; summary: string; url: string }) => Promise<void>;
  onSetThumbnail?: SetThumbnailFn;
  onDeleteCard?: (id: string) => Promise<void>;
  blockId?: string;
  /** Droppable container ID for cross-container card DnD */
  containerId?: string;
  /** Prefixed card IDs for this container's sortable context */
  sortableCardIds?: string[];
  /** Whether a card drag is currently active (for showing drop zone) */
  isDragActive?: boolean;
}

export default function CardGrid({
  cards,
  loading,
  hasSection,
  onAddCard,
  onUpdateCard,
  onSetThumbnail,
  onDeleteCard,
  blockId,
  containerId,
  sortableCardIds,
  isDragActive,
}: Props) {
  // Make this grid a droppable target for cross-container card movement
  const { setNodeRef, isOver } = useDroppable({ id: containerId ?? 'card-grid-fallback' });

  if (!hasSection) return <EmptyState hasSection={false} />;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (cards.length === 0 && !onAddCard && !isDragActive) {
    return <EmptyState hasSection />;
  }

  const showDropZone = isDragActive && containerId;
  const gridContent = (
    <Box
      ref={containerId ? setNodeRef : undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 2,
        minHeight: showDropZone && cards.length === 0 ? 80 : undefined,
        borderRadius: 1,
        border: isOver ? '2px dashed' : showDropZone ? '2px dashed transparent' : undefined,
        borderColor: isOver ? 'primary.main' : undefined,
        bgcolor: isOver ? '#EFF6FF' : undefined,
        transition: 'background-color 150ms ease-in-out, border-color 150ms ease-in-out',
        p: isOver ? 1 : showDropZone ? 1 : 0,
      }}
    >
      {cards.map((card) =>
        sortableCardIds ? (
          <SortableCard
            key={card.id}
            card={card}
            onUpdate={onUpdateCard}
            onSetThumbnail={onSetThumbnail}
            onDelete={onDeleteCard}
          />
        ) : (
          <PortalCard
            key={card.id}
            card={card}
            onUpdate={onUpdateCard}
            onSetThumbnail={onSetThumbnail}
            onDelete={onDeleteCard}
          />
        ),
      )}
      {onAddCard && <AddCardInline onSave={onAddCard} blockId={blockId} />}
    </Box>
  );

  // Wrap in SortableContext if we have sortable card IDs
  if (sortableCardIds) {
    return (
      <SortableContext items={sortableCardIds} strategy={rectSortingStrategy}>
        {gridContent}
      </SortableContext>
    );
  }

  return gridContent;
}
